import yasml from '@thirtytech/yasml';
import { useAtomValue } from 'jotai/index';
import { seatingChartFiltersAtom, selectedEventAtom, selectedMarketplaceEventAtom } from '../../data/atoms';
import { type BrokerNerdsBarkerCoreModelsDTISale, BrokerNerdsBarkerCoreModelsPricingRuleFilters, useGetDtiEventsEventIdMarketplaceSeatgeek } from '../../api';
import { useMemo } from 'react';
import { matchesRowFilter } from '../MarketListings/marketListingsFilter';
import { useLocalStorage } from '@mantine/hooks';
import { MarketplaceResultsSelection } from '../MarketplaceResults/MarketplaceResults.types';

function filterSeatGeekSales(sales: BrokerNerdsBarkerCoreModelsDTISale[], filters: BrokerNerdsBarkerCoreModelsPricingRuleFilters) {
  const rowParts = filters.rows?.split(/\s+/);

  // Hoist the lowercased section-id lookup out of the per-sale loop: previously each sale
  // rebuilt `sectionIds.map(toLowerCase)` and did an O(m) `.includes`. A precomputed Set gives
  // identical membership results with O(1) lookups (case-insensitive compare is unchanged).
  const sectionIdSet = filters.sectionIds && filters.sectionIds.length > 0 ? new Set(filters.sectionIds.map((x) => x.toLowerCase())) : null;

  return sales.filter((sale) => {
    if (filters.noFilters) {
      return true;
    }

    if (sectionIdSet && (sale.section === undefined || !sectionIdSet.has(sale.section.toLowerCase()))) {
      return false;
    }

    if (filters.rows) {
      if (!rowParts) {
        return false;
      }

      if (!matchesRowFilter(sale.row ?? 'GA', rowParts)) {
        return false;
      }
    }

    if (filters.splits && filters.splits.length > 0) {
      const quantities = [...Array(sale.quantity).keys()].map((x) => x + 1);
      if (filters.splits.filter((split) => quantities.includes(split)).length === 0) {
        return false;
      }
    }

    if (filters.minPrice && sale.price < filters.minPrice) {
      return false;
    }

    if (filters.maxPrice && sale.price > filters.maxPrice) {
      return false;
    }

    return true;
  });
}

const DTIMarketplaceState = () => {
  const [resultsSelection, setResultsSelection] = useLocalStorage<MarketplaceResultsSelection>({
    key: 'setting-marketplace-results-selection',
    defaultValue: 'Listings',
    getInitialValueInEffect: false,
  });

  const globalSelectedMarketplaceEvent = useAtomValue(selectedMarketplaceEventAtom);
  const selectedEvent = useAtomValue(selectedEventAtom);

  const sgEventId = parseInt(selectedEvent?.eventId ?? 'invalid');

  const {
    data: seatGeekData,
    isFetching: isFetchingSeatGeek,
    refetch: refetchSeatGeek,
  } = useGetDtiEventsEventIdMarketplaceSeatgeek(sgEventId, {
    query: {
      enabled: sgEventId > 0 && resultsSelection === 'Sales' && globalSelectedMarketplaceEvent.marketplaceId === 'SeatGeek',
      select: (data) => data.data.data,
    },
  });

  const salesRaw = useMemo(() => seatGeekData?.sales || [], [seatGeekData]);
  const seatingChartFilters = useAtomValue(seatingChartFiltersAtom);
  const sales = useMemo(() => filterSeatGeekSales(salesRaw, seatingChartFilters), [salesRaw, seatingChartFilters]);

  const salesTotals = useMemo(
    () => ({
      totalListings: salesRaw.length ?? 0,
      totalFilteredListings: sales.length,
      totalTickets: salesRaw.reduce((acc, listing) => acc + (listing.quantity || 0), 0),
      totalFilteredTickets: sales.reduce((acc, listing) => acc + (listing.quantity || 0), 0),
    }),
    [salesRaw, sales],
  );

  return {
    sales,
    salesTotals,
    isFetchingSeatGeek,
    refetchSeatGeek,
    resultsSelection,
    setResultsSelection,
  };
};

export const { Provider: DTIMarketplaceStateProvider, useSelector: useDTIMarketplace } = yasml(DTIMarketplaceState);
